import { AssistantClient } from '@multimodal/web-sdk'
import { assertBrowserWebSocketPolicy, redactConnectionUrl } from './rock5aConnectionContext.js'

const DEFAULT_CLIENT_ROLE = 'business_dashboard'
const CONTEXT_THROTTLE_MS = 300

const developmentOverrideUrl = (process.env.VUE_APP_ASSISTANT_WS_URL || '').trim()
const manualOverrideEnabled = process.env.VUE_APP_ASSISTANT_MANUAL_OVERRIDE_ENABLED === 'true'
const production = process.env.NODE_ENV === 'production'

const config = Object.freeze({
  developmentOverrideUrl,
  manualOverrideEnabled,
  clientRole: (process.env.VUE_APP_ASSISTANT_CLIENT_ROLE || DEFAULT_CLIENT_ROLE).trim(),
  clientId: (process.env.VUE_APP_ASSISTANT_CLIENT_ID || 'business-dashboard').trim(),
})

const assistantClient = new AssistantClient(config.clientId)
let connectPromise = null
let activeEndpoint = null
const diagnostics = {
  url: '',
  connectionState: 'idle',
  browserBlocked: false,
  closeCode: null,
  lastError: '',
  tokenState: 'missing',
}
assistantClient.onDisconnect(() => {
  diagnostics.connectionState = 'disconnected'
  diagnostics.closeCode = assistantClient.lastCloseCode || null
  diagnostics.lastError = assistantClient.lastCloseReason || diagnostics.lastError
})

function usesDemoPort(url) {
  try {
    const base = typeof window !== 'undefined' ? window.location.href : 'http://localhost/'
    return new URL(url, base).port === '8780'
  } catch (_) {
    return /(^|:)8780(?:\/|$)/.test(url)
  }
}

export function getAssistantClient() {
  return assistantClient
}

export function getAssistantClientConfig() {
  return {
    ...config,
    url: production && !manualOverrideEnabled ? '' : developmentOverrideUrl,
    tokenState: diagnostics.tokenState,
  }
}

export function getAssistantDiagnostics() {
  return {
    ...diagnostics,
    closeCode: assistantClient.lastCloseCode || diagnostics.closeCode,
    url: redactConnectionUrl(diagnostics.url),
    sessionId: assistantClient.sessionId || '',
    clientRole: config.clientRole,
  }
}

function resolveEndpoint(context) {
  if (context && context.assistantWsUrl) {
    return { url: context.assistantWsUrl, token: context.token || '', source: context.discoverySource }
  }
  if (!production || manualOverrideEnabled) {
    return {
      url: developmentOverrideUrl,
      token: process.env.VUE_APP_ASSISTANT_TOKEN || '',
      source: manualOverrideEnabled ? 'MANUAL_ENV_ROLLBACK' : 'DEVELOPMENT_OVERRIDE',
    }
  }
  return { url: '', token: '', source: 'PRODUCTION_CONTEXT_REQUIRED' }
}

export async function connectAssistantClient(context = null) {
  const endpoint = resolveEndpoint(context)
  if (!endpoint.url) {
    diagnostics.connectionState = 'blocked'
    diagnostics.lastError = 'Rock5aConnectionContext is required in production'
    return false
  }
  if (usesDemoPort(endpoint.url)) {
    const error = new Error('VUE_APP_ASSISTANT_WS_URL must not target the 8780 demo service')
    error.code = 'DEMO_SERVICE_FORBIDDEN'
    diagnostics.connectionState = 'blocked'
    diagnostics.lastError = error.message
    throw error
  }
  try {
    assertBrowserWebSocketPolicy({
      controlWsUrl: context?.controlWsUrl || endpoint.url,
      audioWsUrl: context?.audioWsUrl || endpoint.url,
      assistantWsUrl: endpoint.url,
    })
  } catch (error) {
    diagnostics.connectionState = 'blocked'
    diagnostics.browserBlocked = Boolean(error.browserBlocked)
    diagnostics.lastError = error.message
    throw error
  }
  const endpointKey = `${endpoint.url}|${endpoint.token}`
  if (assistantClient.isConnected && activeEndpoint === endpointKey) return true
  if (assistantClient.isConnected || activeEndpoint !== endpointKey) assistantClient.disconnect()
  activeEndpoint = endpointKey
  diagnostics.url = endpoint.url
  diagnostics.tokenState = endpoint.token ? 'configured' : 'missing'
  diagnostics.connectionState = 'connecting'
  diagnostics.browserBlocked = false
  diagnostics.lastError = ''
  if (!connectPromise) {
    connectPromise = assistantClient.connect({
      url: endpoint.url,
      token: endpoint.token,
      clientRole: config.clientRole,
      heartbeatIntervalMs: 5000,
      reconnect: {
        enabled: true,
        baseDelayMs: 1000,
        maxDelayMs: 30000,
        maxAttempts: 10,
      },
    }).then(() => {
      diagnostics.connectionState = 'connected'
      return true
    }).catch((error) => {
      diagnostics.connectionState = 'error'
      diagnostics.lastError = error.message || 'assistant_connection_failed'
      throw error
    }).finally(() => { connectPromise = null })
  }
  return connectPromise
}

export function disconnectAssistantClient() {
  connectPromise = null
  assistantClient.disconnect()
  activeEndpoint = null
  diagnostics.connectionState = 'disconnected'
  diagnostics.closeCode = assistantClient.lastCloseCode || null
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue)
  if (!value || typeof value !== 'object') return value
  return Object.keys(value).sort().reduce((result, key) => {
    result[key] = stableValue(value[key])
    return result
  }, {})
}

export function createAssistantContextReporter(client = assistantClient, throttleMs = CONTEXT_THROTTLE_MS) {
  let timer = null
  let pending = null
  let pendingKey = ''
  let lastSentKey = ''
  let lastSentAt = 0

  function flush() {
    if (timer) clearTimeout(timer)
    timer = null
    if (!pending || !client.isConnected || pendingKey === lastSentKey) return false
    client.updateContext(pending)
    lastSentKey = pendingKey
    lastSentAt = Date.now()
    pending = null
    pendingKey = ''
    return true
  }

  function report(context) {
    const next = { ...context, clientRole: config.clientRole }
    const key = JSON.stringify(stableValue(next))
    if (key === lastSentKey) return false
    if (key === pendingKey) return client.isConnected ? flush() : false
    pending = next
    pendingKey = key
    const remaining = Math.max(0, throttleMs - (Date.now() - lastSentAt))
    if (remaining === 0) return flush()
    if (!timer) timer = setTimeout(flush, remaining)
    return true
  }

  function dispose() {
    if (timer) clearTimeout(timer)
    timer = null
    pending = null
    pendingKey = ''
  }

  return { report, flush, dispose }
}
